import React, { useState, useEffect, useRef } from 'react';
import { GraduationCap, Code, Activity, CheckCircle } from 'lucide-react';
import { useTheme } from './theme-provider';
import CalendlyModal from '../../components/CalendlyModal';

const IndustryApplications = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sectionRef = useRef(null);
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  // Industry applications data with Chatbase-inspired design
  const industries = [
    {
      icon: <GraduationCap size={32} className="text-blue-600" />,
      title: "Education Platform",
      description: "Provide 24/7 academic support, answer course-related questions, guide students through enrollment processes, and offer personalized learning assistance.",
      features: ["Course guidance", "Enrollment assistance", "Homework help", "Resource recommendations"],
      gradient: "from-blue-50 to-blue-100",
      iconBg: "bg-blue-500",
      borderColor: "border-blue-200"
    },
    {
      icon: <Code size={32} className="text-purple-600" />,
      title: "SaaS & Technology",
      description: "Guide users through product features, troubleshoot technical issues, assist with onboarding, and provide instant answers to frequently asked questions.",
      features: ["Feature tutorials", "Technical troubleshooting", "Onboarding assistance", "API documentation"],
      gradient: "from-purple-50 to-purple-100",
      iconBg: "bg-purple-500",
      borderColor: "border-purple-200"
    },
    {
      icon: <Activity size={32} className="text-emerald-600" />,
      title: "Finance & Healthcare",
      description: "Securely answer policy questions, explain services, schedule appointments, and provide compliant assistance while maintaining privacy and regulatory requirements.",
      features: ["Policy explanations", "Secure information access", "Appointment scheduling", "Regulatory compliance"],
      gradient: "from-emerald-50 to-emerald-100",
      iconBg: "bg-emerald-500",
      borderColor: "border-emerald-200"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className={`py-24 relative ${
        isDarkMode 
          ? 'bg-gray-900' 
          : 'bg-gray-50'
      }`}
    >
      <div className="container mx-auto px-4">
        {/* Header Section - Chatbase style */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
            {/* Left side - Tag and Title */}
            <div className="lg:w-1/2">
              {/* Highlights tag */}
              <div className="inline-flex items-center mb-6">
                <span className={`text-sm font-medium px-3 py-1 rounded-full inline-flex items-center gap-2 ${
                  isDarkMode 
                    ? 'bg-gray-800 text-gray-300 border border-gray-700' 
                    : 'bg-gray-100 text-gray-700 border border-gray-200'
                }`}>
                  <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                  Industry Solutions
                </span>
              </div>
              
              <h2 className={`text-4xl md:text-5xl font-bold leading-tight ${
                isDarkMode ? 'text-white' : 'text-black'
              }`}>
                The complete platform for AI support agents
              </h2>
            </div>
            
            {/* Right side - Description */}
            <div className="lg:w-1/2 lg:pt-16">
              <p className={`text-lg leading-relaxed ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                SmushChat is designed for building AI support agents that solve your customers' hardest problems while improving business outcomes.
              </p>
            </div>
          </div>
        </div>

        {/* Three Cards Section - Chatbase style */}
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <div 
                key={index}
                className={`rounded-2xl border-2 ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700' 
                    : `bg-white border-gray-200 ${industry.borderColor}`
                } ${
                  isVisible ? 'animate-fade-in' : 'opacity-0'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Card Header with Icon */}
                <div className={`p-6 ${isDarkMode ? 'bg-gray-800' : `bg-gradient-to-br ${industry.gradient}`}`} style={{ borderRadius: '15px' }}>
                  {/* Icon with gradient border */}
                  <div className="relative mb-6">
                    <div className={`w-16 h-16 rounded-xl ${industry.iconBg} flex items-center justify-center text-white relative`}>
                      {industry.icon}
                      {/* Gradient border effect */}
                      <div className={`absolute -bottom-1 -right-1 w-18 h-18 rounded-2xl bg-gradient-to-r from-orange-400 to-purple-500 -z-10`}></div>
                    </div>
                  </div>
                  
                  <h3 className={`text-xl font-bold mb-3 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {industry.title}
                  </h3>
                  
                  <p className={`text-sm leading-relaxed ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {industry.description}
                  </p>
                </div>

                {/* Card Footer with Features */}
                <div className={`p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-t border-gray-100`} style={{ borderRadius: '15px' }}>
                  <div className="space-y-3">
                    {industry.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
      
      {/* Calendly Modal */}
      <CalendlyModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default IndustryApplications;
